/**
 * Single source of truth for hero + guide copy for all Discover topic pages (Visiting Japan).
 * Keyed by slug; no fallback generation — missing slug should be a compile-time error.
 */

export type TopicGuideBullet = {
  title: string;
  description?: string;
};

export type TopicCopy = {
  hero: {
    /** Full hero headline (e.g. "Tokyo food experiences: eat like a local"). */
    headline: string;
    /** Substring to render with gradient; if omitted, headline is used as-is. */
    headlineHighlight?: string;
    description: string;
  };
  guide: {
    intro: string;
    bullets: readonly TopicGuideBullet[];
    closingText: string;
  };
};

const TOPIC_COPY = {
  'tokyo-local-food-experiences': {
    hero: {
      headline: 'Tokyo food experiences: eat like a local',
      headlineHighlight: 'eat like a local',
      description:
        'Discover authentic Tokyo food experiences — from local food tours to hands-on culinary adventures, designed to help you eat like a local.',
    },
    guide: {
      intro:
        'At Peatix, we bring people together through shared cultural experiences. This Tokyo food guide helps travelers discover local food events and experiences they can plan for and book in advance.',
      bullets: [
        {
          title: 'Discover authentic Tokyo food experiences',
          description:
            'Find sushi-making classes, sake festivals, wine tastings, neighborhood food tours, and hands-on cooking workshops hosted by locals.',
        },
        {
          title: 'Plan and book food events before you arrive',
          description:
            'Easily browse and buy tickets to Tokyo food experiences in advance, including limited-capacity events that often sell out.',
        },
        {
          title: 'Support local hosts and food communities',
          description:
            'Connect with organizers sharing everyday Tokyo cuisine, seasonal specialties, and neighborhood food culture.',
        },
        {
          title: 'Get inspired while planning your trip',
          description:
            'Explore traditional tea ceremonies, Japanese sweets tastings, and other food-focused events that fit different travel styles and itineraries.',
        },
      ],
      closingText:
        'Taste the real Tokyo through local food events and experiences you can plan for and enjoy with confidence.',
    },
  },

  'tokyo-traditions-and-culture': {
    hero: {
      headline: 'Tokyo: Tradition and culture',
      headlineHighlight: 'Tradition and culture',
      description:
        "Browse cultural events in Tokyo—local workshops, community meetups, seasonal traditions, and experiences that help you explore the city's heritage.",
    },
    guide: {
      intro:
        'This guide helps travelers discover Tokyo tradition and culture experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Tokyo: Tradition and culture experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'tokyo-food-and-drink': {
    hero: {
      headline: 'Tokyo: Food and drink experiences',
      headlineHighlight: 'Food and drink experiences',
      description:
        'Browse upcoming food and drink events in Tokyo—from tastings and tours to hands-on classes you can plan for and book in advance.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Tokyo food and drink experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Tokyo: Food and drink experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'tokyo-anime-and-otaku-culture': {
    hero: {
      headline: 'Tokyo: Anime and otaku',
      headlineHighlight: 'Anime and otaku',
      description:
        'Find anime and otaku events in Tokyo—from screenings and fan meetups to themed experiences you can add to your itinerary.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Tokyo anime and otaku experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Tokyo: Anime and otaku experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'tokyo-nightlife-and-bar-scene': {
    hero: {
      headline: 'Tokyo: Nightlife and bars',
      headlineHighlight: 'Nightlife and bars',
      description:
        'Browse Tokyo nightlife experiences—bar events, tastings, and social meetups you can plan for and book ahead.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Tokyo nightlife and bars experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Tokyo: Nightlife and bars experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'tokyo-sake-and-brewery-tours': {
    hero: {
      headline: 'Tokyo: Sake and brewery experiences',
      headlineHighlight: 'Sake and brewery experiences',
      description:
        'Explore Tokyo sake and brewery experiences—tastings, community events, and drink-focused meetups you can book in advance.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Tokyo sake and brewery experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Tokyo: Sake and brewery experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'tokyo-seasonal-festivals-and-events': {
    hero: {
      headline: 'Tokyo: Seasonal festivals',
      headlineHighlight: 'Seasonal festivals',
      description:
        'Browse Tokyo seasonal festivals and events—find experiences that match your travel dates and book ticketed events in advance.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Tokyo seasonal festivals experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Tokyo: Seasonal festivals experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'kyoto-local-food-experiences': {
    hero: {
      headline: 'Kyoto: Eat like a local',
      headlineHighlight: 'Eat like a local',
      description:
        'Browse local Kyoto food experiences—seasonal tastings, small events, and community gatherings you can book in advance.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Kyoto eat like a local experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Kyoto: Eat like a local experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'kyoto-traditions-and-culture': {
    hero: {
      headline: 'Kyoto: Tradition and culture',
      headlineHighlight: 'Tradition and culture',
      description:
        "Explore Kyoto culture through local events—workshops, performances, and experiences inspired by the city's history and seasonal traditions.",
    },
    guide: {
      intro:
        'This guide helps travelers discover Kyoto tradition and culture experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Kyoto: Tradition and culture experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'kyoto-culture-and-experiences': {
    hero: {
      headline: 'Kyoto: Cultural experiences',
      headlineHighlight: 'Cultural experiences',
      description:
        'Discover Kyoto cultural experiences—workshops, events, and community gatherings that fit easily into your trip.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Kyoto cultural experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Kyoto: Cultural experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'osaka-local-food-experiences': {
    hero: {
      headline: 'Osaka: Eat like a local',
      headlineHighlight: 'Eat like a local',
      description:
        'Browse local Osaka food experiences—tastings, community events, and hands-on activities you can book ahead of your trip.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Osaka eat like a local experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Osaka: Eat like a local experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'osaka-food-and-drink': {
    hero: {
      headline: 'Osaka: Food and drink experiences',
      headlineHighlight: 'Food and drink experiences',
      description:
        'Explore Osaka food and drink experiences—from casual tastings to local events you can plan for and book in advance.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Osaka food and drink experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Osaka: Food and drink experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'osaka-traditions-and-culture': {
    hero: {
      headline: 'Osaka: Tradition and culture',
      headlineHighlight: 'Tradition and culture',
      description:
        'Discover Osaka tradition and culture through local events—workshops, meetups, and community experiences.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Osaka tradition and culture experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Osaka: Tradition and culture experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'japan-top-experiences': {
    hero: {
      headline: 'Japan: Must do experiences',
      headlineHighlight: 'Must do experiences',
      description:
        'Browse standout experiences across Japan—events and activities you can plan for, book, and build into your itinerary.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Japan must do experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Japan: Must do experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'japan-food-and-drink-experiences': {
    hero: {
      headline: 'Japan: Food experiences',
      headlineHighlight: 'Food experiences',
      description:
        'Explore food experiences across Japan—from tastings and tours to hands-on workshops you can book in advance.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Japan food experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Japan: Food experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'japan-culture-and-experiences': {
    hero: {
      headline: 'Japan: Cultural experiences',
      headlineHighlight: 'Cultural experiences',
      description:
        'Discover cultural experiences across Japan—events, workshops, and local gatherings you can add to your itinerary.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Japan cultural experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Japan: Cultural experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'japan-anime-and-otaku': {
    hero: {
      headline: 'Japan: Anime and otaku experiences',
      headlineHighlight: 'Anime and otaku experiences',
      description:
        'Discover anime and otaku experiences across Japan that include fan events, meetups, and themed activities you can plan for and book ahead.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Japan anime and otaku experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Japan: Anime and otaku experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },

  'japan-festivals-and-celebrations': {
    hero: {
      headline: 'Japan: Festivals and events',
      headlineHighlight: 'Festivals and events',
      description:
        'Browse festivals and events across Japan—seasonal celebrations and ticketed experiences you can plan for and book in advance.',
    },
    guide: {
      intro:
        'This guide helps travelers discover Japan festivals and events experiences they can plan for and book in advance.',
      bullets: [
        { title: 'Discover Japan: Festivals and events experiences', description: 'Browse events and activities hosted by local communities and organizers.' },
        { title: 'Plan around your travel dates', description: 'Choose experiences that fit your itinerary and reserve tickets ahead of time.' },
        { title: 'Book with confidence', description: 'Review event details and secure your spot for limited-capacity experiences.' },
        { title: 'Explore beyond the guidebooks', description: 'Use local events to discover neighborhoods and communities you might otherwise miss.' },
      ],
      closingText: 'Build a memorable itinerary through local events and experiences you can plan for and enjoy.',
    },
  },
} as const;

export type TopicSlug = keyof typeof TOPIC_COPY;

export function getTopicCopy(slug: TopicSlug): TopicCopy {
  const copy = TOPIC_COPY[slug];
  if (!copy) {
    throw new Error(`Missing topic copy for slug: ${slug}. Add an entry to src/content/discover/topics.ts.`);
  }
  return copy;
}

/** Compile-time safety: use this when the slug is dynamic (e.g. from route). Returns null if slug is not a valid TopicSlug. */
export function getTopicCopyIfExists(slug: string): TopicCopy | null {
  if (slug in TOPIC_COPY) {
    return TOPIC_COPY[slug as TopicSlug];
  }
  return null;
}

export { TOPIC_COPY };
